import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  cn,
  Label,
  ProgressIndicator,
  SwitchButton,
  toast,
  ToolButton,
} from '@crabs-ai/ds-react';
import { Pause, PlayArrow } from '@crabs-ai/web-icons';
import { useLocale, useTranslations } from 'next-intl';
import ImageWithFallback from '@/shared/ui/images/ImageWithFallback';
import {
  PremiumVendors,
  VideoEditorTTSVoice,
} from '@/shared/apis/tts/get-tts-voices';
import { useGetTTSVoiceUrlApi } from '@/shared/apis/tts/get-tts-voice-url';
import { useTtsDialog } from '@/shared/components/dialog/TtsDialog/TtsDialogContext';
import { getTTSVoiceUrlApi } from '@/shared/apis/tts/get-tts-voice-url';
import { isTtsBlockedError } from '@/shared/lib/tts-blocked-error';
import {
  DEFAULT_EMOTION,
  DEFAULT_PITCH,
} from '@/shared/constants/default-voice';
import {
  GeminiPreviewLanguage,
  useGeminiPreviewUrl,
} from '@/shared/components/dialog/TtsDialog/useGeminiPreviewUrl';

/**
 * 재생 모드.
 * - `gemini-dynamic`: Gemini + style 지정. 실시간 preview API + blob URL (캐시).
 * - `detail-static`: detail 모드에서 selectedVoice의 설정(tempo/pitch/emotion)대로
 *   사전 렌더된 S3 정적 샘플 URL 사용.
 * - `list-static`: list 모드. normal emotion·default speed·pitch 0의 정적 샘플.
 */
type PlaybackMode = 'gemini-dynamic' | 'detail-static' | 'list-static';

/** next-intl locale 문자열 → BE Language enum. */
const localeToLanguage = (locale: string): GeminiPreviewLanguage =>
  locale === 'ko' ? 'KOREAN' : 'ENGLISH';

/** `preservesPitch` 표준 + 구형 브라우저 vendor prefix 2종 같이 설정. */
const setPreservesPitch = (audio: HTMLAudioElement, value: boolean) => {
  audio.preservesPitch = value;
  (
    audio as HTMLAudioElement & { mozPreservesPitch?: boolean }
  ).mozPreservesPitch = value;
  (
    audio as HTMLAudioElement & { webkitPreservesPitch?: boolean }
  ).webkitPreservesPitch = value;
};

export default function TtsVoiceCard({
  voice,
  onClick,
  isDetail = false,
  enableStyleDirective = false,
}: {
  voice: VideoEditorTTSVoice;
  onClick?: (e: React.MouseEvent) => void;
  isDetail?: boolean;
  enableStyleDirective?: boolean;
}) {
  const t = useTranslations();
  const locale = useLocale();
  const {
    selectedVoice,
    pitch,
    tempo,
    selectedEmotion,
    style,
    currentPlayingId,
    playPreviewAudio,
    stopCurrentAudio,
    previewLanguage,
  } = useTtsDialog();

  // preview 재생 언어: caller override(previewLanguage) 우선, 없으면 UI locale 기준(기존 동작).
  const resolvedPreviewLanguage: GeminiPreviewLanguage =
    previewLanguage ?? localeToLanguage(locale);

  // hover 상태 관리 (isDetail이 false일 때만 사용)
  const [isHovered, setIsHovered] = useState(false);

  // Gemini preview 훅 (내부에서 캐시·언마운트 cleanup·로딩 state 관리)
  const { getUrl: getGeminiPreviewUrl, isLoading: isPreviewLoading } =
    useGeminiPreviewUrl();

  // 현재 카드의 고유 ID (detail/list 구분)
  const cardPlayingId = `${isDetail ? 'detail' : 'list'}-${voice.code}`;

  // Gemini + style 있을 때 preview API를 써야 함 (S3 정적 샘플에 style 반영 안 됨)
  const shouldUsePreview =
    enableStyleDirective &&
    isDetail &&
    voice.vendor === 'gemini' &&
    !!style?.trim();

  // 현재 재생 모드 결정 (분기 조건을 모드 이름으로 승격)
  const playbackMode: PlaybackMode = shouldUsePreview
    ? 'gemini-dynamic'
    : isDetail
      ? 'detail-static'
      : 'list-static';

  // detail일 때만 selectedVoice의 설정으로 API 호출
  const { data: ttsUrlData } = useGetTTSVoiceUrlApi(
    {
      voice: selectedVoice?.code ?? voice.code,
      language: resolvedPreviewLanguage,
      pitch: pitch,
      emotion: selectedEmotion?.code ?? DEFAULT_EMOTION,
      speedFactor: tempo,
    },
    {
      enabled: isDetail && selectedVoice?.code === voice.code && pitch === 0,
    },
  );

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [progress, setProgress] = useState(0);

  // 현재 카드가 재생 중인지 확인
  const isPlaying = currentPlayingId === cardPlayingId;

  // detail 모드일 때의 audio URL
  const audioUrl = isDetail ? ttsUrlData?.data : null;

  // detail 모드일 때의 오디오 설정
  useEffect(() => {
    if (!isDetail || !audioUrl) {
      setProgress(0);
      return;
    }

    const audio = new Audio(audioUrl);
    audioRef.current = audio;

    const handleTimeUpdate = () => {
      if (!audio.duration || Number.isNaN(audio.duration)) {
        setProgress(0);
        return;
      }

      setProgress(Math.min((audio.currentTime / audio.duration) * 100, 100));
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);

    return () => {
      audio.pause();
      audio.removeEventListener('timeupdate', handleTimeUpdate);

      if (audioRef.current === audio) {
        audioRef.current = null;
      }

      setProgress(0);
    };
  }, [audioUrl, isDetail]);

  // detail 모드에서 tempo/pitch/emotion/voice/style 변경 시 재생 중단.
  // (style도 cache miss를 유발하므로 함께 정지시켜 기존 blob URL 재사용 audio가 revoke 후까지 남지 않도록 함)
  useEffect(() => {
    if (isDetail && isPlaying) {
      stopCurrentAudio();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tempo, pitch, selectedEmotion, selectedVoice, style]);

  // 언마운트 시 이 카드가 재생 중이면 우선 정지시키고 나서야 훅이 blob URL을 revoke할 수 있게 한다.
  // 그렇지 않으면 상위 Provider(currentAudioRef)가 revoke된 blob URL을 참조하는 Audio를 보유.
  // `playingRef`는 최신 isPlaying을 cleanup에서 읽기 위한 mirror — commit 단계에만 갱신(concurrent 안전).
  const playingRef = useRef(false);
  useEffect(() => {
    playingRef.current = isPlaying;
  }, [isPlaying]);
  useEffect(() => {
    return () => {
      if (playingRef.current) {
        stopCurrentAudio();
      }
    };
    // stopCurrentAudio은 Context 안정 참조
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** 모드별로 재생할 audio URL을 획득. */
  const resolvePlaybackUrl = useCallback(
    async (mode: PlaybackMode): Promise<string | undefined> => {
      if (isDetail && pitch !== 0) {
        toast({type: 'error', description: '선택한 음높이의 저장된 미리듣기 견본은 없습니다. 생성 후 실제 결과를 확인해 주세요.'});
        return undefined;
      }
      const language = resolvedPreviewLanguage;
      switch (mode) {
        case 'gemini-dynamic': {
          // cache miss 시 이전 blob URL이 revoke되므로, 해당 URL을 참조 중인
          // Audio가 남아있지 않도록 훅의 onBeforeRevoke 콜백에서 정지시킨다.
          return getGeminiPreviewUrl(
            {
              voice: voice.code,
              style: style?.trim() ?? '',
              emotion: selectedEmotion?.code ?? 'normal',
              language,
              text: t('Common.component.dialog.tts-dialog.preview-sample-text'),
            },
            () => {
              if (playingRef.current) stopCurrentAudio();
            },
          );
        }
        case 'detail-static':
          return audioUrl ?? undefined;
        case 'list-static': {
          const defaultEmotion = voice.emotions.at(0);
          const response = await getTTSVoiceUrlApi({
            voice: voice.code,
            language,
            pitch: DEFAULT_PITCH,
            emotion: defaultEmotion?.code ?? DEFAULT_EMOTION,
            speedFactor: voice.tempoDefault,
          });
          if(!response.data)toast({type:'error',description:response.message||'저장된 목소리 견본이 없습니다. 자동 생성하지 않았습니다.'});
          return response.data;
        }
      }
    },
    [
      resolvedPreviewLanguage,
      getGeminiPreviewUrl,
      voice.code,
      voice.emotions,
      voice.tempoDefault,
      style,
      selectedEmotion,
      t,
      audioUrl,
      stopCurrentAudio,
      isDetail,
      pitch,
    ],
  );

  /** 재생 URL로 Audio 인스턴스 생성 + playbackRate/preservesPitch 세팅. */
  const buildAudio = useCallback(
    (url: string): HTMLAudioElement => {
      const audio = new Audio(url);
      // Waka's stored sample key already includes synthesis speed. Do not apply it twice.
      audio.playbackRate = 1;
      setPreservesPitch(audio, true);
      return audio;
    },
    [tempo, voice.tempoDefault],
  );

  // 재생 버튼 클릭 핸들러
  const handlePlayButtonClick = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();

      // 이미 재생 중이면 중지
      if (isPlaying) {
        stopCurrentAudio();
        return;
      }

      try {
        const url = await resolvePlaybackUrl(playbackMode);
        if (!url) return;

        const audio = buildAudio(url);

        if (isDetail) {
          // detail 모드: 로컬 audioRef + progress 트래킹
          audioRef.current = audio;
          const handleTimeUpdate = () => {
            if (!audio.duration || Number.isNaN(audio.duration)) {
              setProgress(0);
              return;
            }
            setProgress(
              Math.min((audio.currentTime / audio.duration) * 100, 100),
            );
          };
          audio.addEventListener('timeupdate', handleTimeUpdate);
        }

        // 전역 재생 관리 사용 (detail/list 구분된 ID 사용)
        playPreviewAudio(cardPlayingId, audio, {
          onError: (error) => {
            console.error('Failed to play audio:', error);
            if (isTtsBlockedError(error)) return;
            toast({
              type: 'error',
              description: t(
                'Common.component.dialog.tts-dialog.preview-failed',
              ),
              toastOptions: {
                position: 'top-center',
              },
            });
          },
        });
      } catch (error) {
        console.error('Failed to play audio:', error);
        // 422 정책 차단(1031/1032) 은 전역 TtsBlockedDialog 가 흡수 — 추가 toast skip.
        if (isTtsBlockedError(error)) return;
        toast({
          type: 'error',
          description: t('Common.component.dialog.tts-dialog.preview-failed'),
          toastOptions: {
            position: 'top-center',
          },
        });
      }
    },
    [
      isPlaying,
      stopCurrentAudio,
      isDetail,
      playbackMode,
      resolvePlaybackUrl,
      buildAudio,
      playPreviewAudio,
      cardPlayingId,
      t,
    ],
  );

  // hover 또는 재생 중일 때 재생 버튼 표시 (isDetail이 false일 때)
  const showPlayButton = !isDetail && (isHovered || isPlaying);

  return (
    <div
      className={cn(
        'flex flex-col gap-2xs px-sm py-xs',
        !isDetail && 'cursor-pointer',
      )}
      onClick={onClick}
      onMouseEnter={() => !isDetail && setIsHovered(true)}
      onMouseLeave={() => !isDetail && setIsHovered(false)}
    >
      <div className="flex h-[51px] w-full items-center gap-2xs">
        <div
          className="relative aspect-square size-[48px] overflow-hidden rounded-2xs bg-sys-bg-surface-primary"
          onMouseEnter={() => !isDetail && setIsHovered(true)}
        >
          {voice.thumbnailUrl && voice.thumbnailUrl !== '' && (
            <ImageWithFallback
              src={voice.thumbnailUrl}
              alt={'thumbnailUrl'}
              fill={true}
            />
          )}
          {/* hover 시 재생 버튼 표시 (애니메이션) */}
          {showPlayButton && (
            <div
              className={cn(
                'absolute inset-0 flex items-center justify-center',
              )}
            >
              <SwitchButton
                isOn={isPlaying}
                type="play/pause"
                size="xsmall"
                onClick={handlePlayButtonClick}
                className="size-[32px]"
              />
            </div>
          )}
        </div>
        <div className="flex flex-col">
          <div className="flex items-center gap-2xs">
            <span className="text-title-medium text-sys-cont-neutral-default">
              {locale === 'ko' ? voice.nameKorean : voice.name}
            </span>
            {PremiumVendors.includes(voice.vendor) && (
              <Label
                color="primary"
                size="small"
              >
                {locale === 'ko' ? '고품질' : 'HQ'}
              </Label>
            )}
            {voice.isNew && (
              <Label
                color="red"
                size="small"
              >
                New
              </Label>
            )}
          </div>
          {voice.description && voice.description !== '' && (
            <div className="text-body-small">
              {locale === 'ko' ? voice.descriptionKorean : voice.description}
            </div>
          )}
        </div>
      </div>

      {isDetail && (
        <div className="flex w-full items-center gap-2xs">
          <ToolButton
            size="sm"
            aria-label={`${voice.nameKorean||voice.name} 견본 ${isPlaying?'정지':'듣기'}`}
            onClick={handlePlayButtonClick}
            // Gemini+style 모드는 audioUrl 불필요 (preview API 직접 호출)
            disabled={(!audioUrl && !shouldUsePreview) || isPreviewLoading}
            aria-pressed={isPlaying}
            aria-busy={isPreviewLoading}
          >
            {isPreviewLoading ? (
              <span
                className="inline-block size-[14px] animate-spin rounded-full border-2 border-current border-t-transparent"
                aria-label={t('Common.component.dialog.tts-dialog.loading')}
              />
            ) : isPlaying ? (
              <Pause />
            ) : (
              <PlayArrow />
            )}
          </ToolButton>
          <ProgressIndicator value={progress} />
          {!audioUrl&&ttsUrlData?.message&&<span role="status" className="text-caption-small">{ttsUrlData.message}</span>}
          {shouldUsePreview && !isPreviewLoading && (
            <span className="text-caption-small text-sys-cont-neutral-light">
              {t('Common.component.dialog.tts-dialog.realtime-generation')}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
